import { useEffect, useMemo, useRef, useState } from 'react';
import { listApprovals, decideApproval, cancelApproval } from '@/data/approvals';
import { ApiError } from '@/lib/api';
import {
  createDecisionRunner,
  payloadPreviewRows,
  proposedActionSummary,
  recommendationText,
  confirmAffirmation,
  isPending,
  type PublicApproval,
  type DecisionDeps,
} from './approvalActionModel.ts';

/* The live decision wiring: the runner generates the STABLE idempotency key and
 * hands it here, so this only forwards to the data client (which sends it as
 * X-Bksesh-Idempotency-Key). Tests inject a mock DecisionDeps instead. */
const liveDecisionDeps: DecisionDeps = {
  decide: (id, decision, key, note) => decideApproval(id, decision, key, note),
  cancel: (id, key) => cancelApproval(id, key),
};

/* ----------------------------------------------------------------------------
 * ApprovalActionPanel (automation pilot, Option a — owner 2026-07-19).
 *
 * Aura VISIBLY shows the human EXACTLY what will be created before they approve:
 * for every pending (`proposed`) approval it renders the full, un-summarised
 * proposed_action.payload (the concrete CRM contact fields) plus the advisory
 * recommendation, THEN offers Approve / Reject / Cancel. Approve requires an
 * explicit click and a second confirm that affirms exactly what will be created —
 * no auto-approve, no bulk-approve.
 *
 * This panel NEVER creates approvals and calls no non-allowlisted action — only
 * list / decide / cancel. It shows only what the API returns (the API already
 * gates to ws=1 + ROLE_OWNER + active pilot). Fail-closed rendering: when nothing
 * is pending, or the surface is unwired (503) / not found (404), it renders
 * NOTHING (no error noise). A 403 (non-owner / pilot-restricted) shows one honest
 * line instead of the list.
 * -------------------------------------------------------------------------- */

const primaryBtn =
  'inline-flex items-center justify-center rounded-full border border-ai bg-ai-wash/60 px-3 py-1.5 text-xs font-semibold text-text-primary transition-colors duration-fast hover:bg-ai-wash disabled:opacity-50 disabled:cursor-not-allowed max-lg:min-h-[44px]';
const dangerBtn =
  'inline-flex items-center justify-center rounded-sm border border-border-default px-2 py-1.5 text-[11px] text-text-secondary transition-colors duration-fast hover:bg-hover hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed max-lg:min-h-[44px]';
const ghostBtn =
  'inline-flex items-center justify-center rounded-sm px-2 py-1.5 text-[11px] text-text-tertiary transition-colors duration-fast hover:bg-hover hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed max-lg:min-h-[44px]';

function AdvisoryHeader({ label }: { label: string }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ai">
        {label}
      </span>
      <span className="rounded-sm border border-ai/30 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-ai">
        Needs your approval
      </span>
    </div>
  );
}

type CardPhase = 'preview' | 'confirm' | 'working' | 'done' | 'error';

/** One pending approval: full payload preview -> explicit Approve/Reject/Cancel
 *  -> confirm affirmation -> single decision call. */
function ApprovalCard({
  approval,
  deps,
  onResolved,
}: {
  approval: PublicApproval;
  deps: DecisionDeps;
  onResolved: (id: number) => void;
}) {
  const [phase, setPhase] = useState<CardPhase>('preview');
  const [error, setError] = useState<string | null>(null);
  const [doneLabel, setDoneLabel] = useState<string>('');
  // A fresh one-shot runner per card: the human click fires decide at most once.
  const runnerRef = useRef(createDecisionRunner(deps));

  const rows = useMemo(() => payloadPreviewRows(approval.proposed_action), [approval.proposed_action]);
  const rec = recommendationText(approval);
  const target = proposedActionSummary(approval.proposed_action);

  const decide = async (kind: 'approve' | 'reject' | 'cancel') => {
    setError(null);
    setPhase('working');
    try {
      const runner = runnerRef.current;
      const outcome =
        kind === 'approve'
          ? await runner.approve(approval)
          : kind === 'reject'
            ? await runner.reject(approval)
            : await runner.cancel(approval);
      if (outcome.status === 'ignored') return; // already fired once
      setDoneLabel(kind === 'approve' ? 'Approved — the record will be created.' : kind === 'reject' ? 'Rejected — nothing was created.' : 'Cancelled — nothing was created.');
      setPhase('done');
      onResolved(approval.id);
    } catch (e) {
      // Never weaken the backend: surface its refusal honestly, change nothing.
      if (e instanceof ApiError && e.status === 403) {
        setError('Only the workspace owner can decide this in the pilot. Nothing was changed.');
      } else if (e instanceof ApiError && (e.status === 503 || e.status === 404)) {
        setError('The automation pilot is not active right now. Nothing was changed.');
      } else {
        setError('That decision could not be recorded just now. Nothing was changed.');
      }
      // Reset the runner so the owner can retry after an honest failure.
      runnerRef.current = createDecisionRunner(deps);
      setPhase('error');
    }
  };

  if (phase === 'done') {
    return (
      <div className="rounded-md border border-ai/25 bg-canvas p-3">
        <p className="text-sm text-text-primary">{doneLabel}</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-ai/25 bg-canvas p-3">
      <div className="mb-2 flex items-center gap-2 text-[10px] text-text-tertiary">
        <span className="rounded-sm border border-border-default px-1.5 py-0.5 font-mono">
          {approval.action_class}
        </span>
        <span className="font-mono">{target}</span>
      </div>

      {(rec.subject || rec.body) && (
        <div className="mb-2">
          {rec.subject && <p className="text-sm font-semibold text-text-primary">{rec.subject}</p>}
          {rec.body && <p className="mt-0.5 whitespace-pre-line text-[12px] leading-relaxed text-text-secondary">{rec.body}</p>}
        </div>
      )}

      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
        Will create this record
      </div>
      {rows.length > 0 ? (
        <dl className="space-y-1">
          {rows.map((row) => (
            <div key={row.key} className="grid grid-cols-[110px_1fr] gap-2 text-xs">
              <dt className="break-words font-mono text-text-tertiary">{row.key}</dt>
              <dd className="break-words text-text-primary">
                {row.value !== '' ? row.value : <span className="text-text-tertiary">—</span>}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-xs text-text-tertiary">
          This item carries no record fields — it is advisory only.
        </p>
      )}

      {phase === 'error' && error && (
        <p className="mt-2 rounded-sm border border-border-default bg-hover/40 px-2 py-1 text-[11px] text-text-secondary">
          {error}
        </p>
      )}

      {phase === 'confirm' ? (
        <div className="mt-3 rounded-md border border-ai/40 bg-ai-wash/40 p-2">
          <p className="text-[12px] text-text-primary">{confirmAffirmation(approval)}</p>
          <div className="mt-2 flex items-center gap-2">
            <button type="button" onClick={() => void decide('approve')} className={primaryBtn}>
              Yes, create it
            </button>
            <button type="button" onClick={() => setPhase('preview')} className={ghostBtn}>
              Back
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            disabled={phase === 'working'}
            onClick={() => setPhase('confirm')}
            className={primaryBtn}
          >
            Approve
          </button>
          <button
            type="button"
            disabled={phase === 'working'}
            onClick={() => void decide('reject')}
            className={dangerBtn}
          >
            Reject
          </button>
          <button
            type="button"
            disabled={phase === 'working'}
            onClick={() => void decide('cancel')}
            className={ghostBtn}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

export function ApprovalActionPanel({ deps = liveDecisionDeps }: { deps?: DecisionDeps } = {}) {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok'; approvals: PublicApproval[] }
    | { kind: 'restricted' }
    | { kind: 'hidden' }
  >({ kind: 'loading' });
  const [resolved, setResolved] = useState<Set<number>>(new Set());

  useEffect(() => {
    let alive = true;
    void (async () => {
      const res = await listApprovals('proposed');
      if (!alive) return;
      if (res.status === 'ok') {
        const pending = res.approvals.filter(isPending);
        setState(pending.length > 0 ? { kind: 'ok', approvals: pending } : { kind: 'hidden' });
      } else if (res.status === 'restricted') {
        setState({ kind: 'restricted' });
      } else {
        setState({ kind: 'hidden' }); // unavailable / unwired -> render nothing
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Fail-closed: nothing pending, unwired, 404, or still loading -> render nothing.
  if (state.kind === 'loading' || state.kind === 'hidden') return null;

  if (state.kind === 'restricted') {
    return (
      <section
        aria-label="Automation approvals"
        className="rounded-md border border-border-default bg-canvas p-3 text-[11px] text-text-tertiary"
      >
        Automation approvals are limited to the workspace owner during the pilot.
      </section>
    );
  }

  const visible = state.approvals.filter((a) => !resolved.has(a.id));
  if (visible.length === 0) return null;

  return (
    <section aria-label="Automation approvals" className="rounded-md border border-ai/40 bg-ai-wash/40 p-3">
      <AdvisoryHeader label="✧ pending approvals" />
      <p className="mb-2 text-[11px] text-text-tertiary">
        Review exactly what will be created, then approve. Nothing is created until you approve.
      </p>
      <div className="space-y-2">
        {visible.map((a) => (
          <ApprovalCard
            key={a.id}
            approval={a}
            deps={deps}
            onResolved={(id) => setResolved((prev) => new Set(prev).add(id))}
          />
        ))}
      </div>
    </section>
  );
}
